import { orderTypeConstants } from "constants/order";
import { ToastContext } from "context/ToastContext";
import { parsePrice } from "helpers/price";
import { Label } from "layout/typography/Label";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { useUpdateOrderMutation } from "store/queries/order";

import {
  validatePaymentPlanForm,
  calculatePreview,
  buildPaymentPlanData,
  getEstimatedCost,
  calculateInitialPayment,
  calculateMonthlyPayment,
  generateInstallments,
  initializeEditorState,
} from "../helpers";
import {
  StyledSectionContainer,
  StyledPriceContainer,
  HorizontalLine,
  StyledTimeline,
  StyledSelectButton,
} from "../styled";
import { PaymentPlanMode, DownPaymentType } from "../types";

function PaymentPlan({
  orderInfo,
  fullPrice,
  clearPrice,
  onPaymentPlanUpdate,
  isAuthenticated,
}: {
  orderInfo: any;
  fullPrice: number;
  clearPrice: number;
  onPaymentPlanUpdate?: () => void;
  isAuthenticated?: boolean;
}) {
  const { current: toastElement } = useContext(ToastContext);

  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editorMode, setEditorMode] = useState<PaymentPlanMode>("months");
  const [numberOfMonths, setNumberOfMonths] = useState<number | null>(null);
  const [downPaymentType, setDownPaymentType] = useState<DownPaymentType>("%");
  const [downPaymentValue, setDownPaymentValue] = useState<number | null>(null);
  const [touched, setTouched] = useState({
    duration: false,
    downPayment: false,
  });

  const activePaymentPlan = useMemo(() => {
    return orderInfo.paymentPlan?.find((plan: any) => plan.active);
  }, [orderInfo]);

  const hasActivePaymentPlan = !!activePaymentPlan;

  const estimatedCost = useMemo(
    () => getEstimatedCost(orderInfo.orderType, clearPrice, fullPrice),
    [orderInfo.orderType, clearPrice, fullPrice]
  );

  // Check if order has clear patient fee (for GFE orders)
  const hasClearPatientFee = useMemo(() => {
    return (
      orderInfo.orderType === orderTypeConstants.GFE &&
      orderInfo.clearPatientFeeValueInPercentage &&
      orderInfo.clearPatientFeeValueInPercentage > 0
    );
  }, [orderInfo.orderType, orderInfo.clearPatientFeeValueInPercentage]);

  // Calculate display value for flat amount without clear patient fee
  const downPaymentDisplayValue = useMemo(() => {
    if (downPaymentType === "$" && hasClearPatientFee && downPaymentValue) {
      return (
        downPaymentValue /
        (1 + orderInfo.clearPatientFeeValueInPercentage / 100)
      );
    }
    return downPaymentValue;
  }, [
    downPaymentValue,
    downPaymentType,
    hasClearPatientFee,
    orderInfo.clearPatientFeeValueInPercentage,
  ]);

  // Calculate max value for display (without fees) when using flat amount
  const estimatedCostDisplay = useMemo(() => {
    if (hasClearPatientFee) {
      return (
        estimatedCost / (1 + orderInfo.clearPatientFeeValueInPercentage / 100)
      );
    }
    return estimatedCost;
  }, [
    estimatedCost,
    hasClearPatientFee,
    orderInfo.clearPatientFeeValueInPercentage,
  ]);

  // Calculate actual payment plan duration (displayed months - 1 since display includes first payment)
  const actualPaymentPlanDuration =
    numberOfMonths !== null ? numberOfMonths - 1 : null;

  // Validation - uses actual duration for backend validation
  const validation = useMemo(
    () =>
      validatePaymentPlanForm(
        editorMode,
        actualPaymentPlanDuration,
        downPaymentValue,
        downPaymentType,
        estimatedCost
      ),
    [
      editorMode,
      actualPaymentPlanDuration,
      downPaymentValue,
      downPaymentType,
      estimatedCost,
    ]
  );

  // Initialize editor
  const initializeEditor = useCallback(() => {
    const state = initializeEditorState(activePaymentPlan);
    setNumberOfMonths(
      state.numberOfMonths !== null ? state.numberOfMonths + 1 : null
    );
    setDownPaymentType(state.downPaymentType);
    setDownPaymentValue(state.downPaymentValue);
  }, [activePaymentPlan]);

  const handleOpenEditor = () => {
    initializeEditor();
    setTouched({ duration: false, downPayment: false });
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
  };

  // Handle mode change - clear values when switching modes
  const handleModeChange = (newMode: PaymentPlanMode) => {
    if (newMode !== editorMode) {
      setEditorMode(newMode);
      // Clear values when switching modes
      setNumberOfMonths(null);
      setDownPaymentValue(null);
      setDownPaymentType(newMode === "downpayment" ? "$" : "%");
      // Reset touched state when switching modes
      setTouched({ duration: false, downPayment: false });
    }
  };

  // Preview calculations
  const previewCalculations = useMemo(
    () =>
      calculatePreview(
        estimatedCost,
        downPaymentType,
        downPaymentValue,
        actualPaymentPlanDuration,
        editorMode
      ),
    [
      estimatedCost,
      downPaymentType,
      downPaymentValue,
      actualPaymentPlanDuration,
      editorMode,
    ]
  );

  // Save handler
  const handleSavePaymentPlan = async () => {
    const paymentPlanData = buildPaymentPlanData(
      editorMode,
      actualPaymentPlanDuration,
      downPaymentType,
      downPaymentValue,
      orderInfo.account?.providerDistributionLogic
    );

    try {
      await updateOrder({
        id: orderInfo.id,
        paymentPlan: [paymentPlanData],
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success",
        detail: hasActivePaymentPlan
          ? "Payment plan updated successfully"
          : "Payment plan created successfully",
        life: 3000,
      });

      setShowEditor(false);

      if (onPaymentPlanUpdate) {
        onPaymentPlanUpdate();
      }
    } catch (error) {
      console.error("Error saving payment plan:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save payment plan. Please try again.",
        life: 5000,
      });
    }
  };

  // Display calculations for existing payment plan
  const initialPayment = useMemo(
    () => calculateInitialPayment(activePaymentPlan, estimatedCost),
    [activePaymentPlan, estimatedCost]
  );

  const paymentPlanDuration = useMemo(() => {
    if (!activePaymentPlan) return 0;
    return activePaymentPlan.paymentPlanDuration;
  }, [activePaymentPlan]);

  const monthlyPayment = useMemo(
    () =>
      calculateMonthlyPayment(
        estimatedCost,
        initialPayment,
        paymentPlanDuration
      ),
    [estimatedCost, initialPayment, paymentPlanDuration]
  );

  const paymentsToShow = useMemo(
    () =>
      activePaymentPlan
        ? generateInstallments(
            orderInfo,
            initialPayment,
            estimatedCost,
            paymentPlanDuration
          )
        : [],
    [
      orderInfo,
      initialPayment,
      estimatedCost,
      paymentPlanDuration,
      activePaymentPlan,
    ]
  );

  const isDownPaymentOnly = useMemo(() => {
    if (!activePaymentPlan) return false;
    return activePaymentPlan.isDownPaymentOnly;
  }, [activePaymentPlan]);

  // Preview installments for editor
  const previewInstallments = useMemo(() => {
    if (
      previewCalculations.isDownPaymentOnly ||
      !previewCalculations.totalPayments
    ) {
      return [];
    }
    return generateInstallments(
      orderInfo,
      previewCalculations.downPayment,
      estimatedCost,
      previewCalculations.totalPayments
    );
  }, [orderInfo, previewCalculations, estimatedCost]);

  const previewLastPaymentDate = useMemo(() => {
    if (previewInstallments.length <= 1) return null;
    return previewInstallments[previewInstallments.length - 1].date;
  }, [previewInstallments]);

  const editorOptions = useMemo(() => {
    const paymentPlanIsEnabledForOrderType = getPaymentPlanEnabled(
      orderInfo.orderType,
      orderInfo.account.paymentPlanTypeEnabled
    );
    const { partialPaymentsEnabled } = orderInfo.account;
    const isDownpaymentOnlyPaymentPlan = activePaymentPlan?.isDownPaymentOnly;
    const options = [];
    // in case we are creating a new payment plan, we need to show options based on account settings
    if (!activePaymentPlan) {
      if (paymentPlanIsEnabledForOrderType) {
        options.push({ label: "Set Number of Months", value: "months" });
      }
      if (partialPaymentsEnabled) {
        options.push({ label: "Downpayment Only", value: "downpayment" });
      }
      return options;
    }
    // in case we are editing a payment plan, if it is an ordinary payment plan, we show payment plan as an option and downpayment only based on account settings
    if (!isDownpaymentOnlyPaymentPlan) {
      options.push({ label: "Set Number of Months", value: "months" });
      if (partialPaymentsEnabled) {
        options.push({ label: "Downpayment Only", value: "downpayment" });
      }
      return options;
    }
    // in case we are editing a downpayment only payment plan, we show payment plan based on account settings and  downpayment only anyway
    if (paymentPlanIsEnabledForOrderType) {
      options.push({ label: "Payment Plan", value: "months" });
    }
    options.push({ label: "Downpayment Only", value: "downpayment" });
    // { label: "Set Monthly Payment", value: "monthly" },
    return options;
  }, [orderInfo, activePaymentPlan]);

  const isDownPaymentSameAsMonthlyPayment = useMemo(() => {
    return (
      Math.abs(
        (downPaymentValue || 0) - (previewCalculations.monthlyPayment || 0)
      ) < 0.1
    );
  }, [downPaymentValue, previewCalculations.monthlyPayment, isDownPaymentOnly]);

  useEffect(() => {
    if (!editorOptions.length) {
      return;
    }
    if (editorOptions.length === 1) {
      setEditorMode(editorOptions[0].value as PaymentPlanMode);
    } else {
      setEditorMode(
        activePaymentPlan?.isDownPaymentOnly ? "downpayment" : "months"
      );
    }
  }, [editorOptions, activePaymentPlan]);

  const generateContent = (item: any, index: any) => {
    return (
      <div className="w-full flex justify-content-between">
        <p
          className={`text-sm font-semibold line-height-2 ${
            index === 0 ? "purple" : "highlighted"
          }`}
        >
          {item.date}
        </p>
        <p className="text-base font-medium text-right line-height-1">
          {parsePrice(item.payment)}
        </p>
      </div>
    );
  };

  const handleChangeNumberOfMonths = (e: any) => {
    setNumberOfMonths(e.value ?? null);
    if (e.value) {
      setDownPaymentType("$");
      const downPaymentValue = estimatedCost / e.value;
      setDownPaymentValue(downPaymentValue);
    }
  };

  // Inline Payment Plan Editor UI
  const renderEditor = () => (
    <StyledSectionContainer className="px-3 pt-0">
      <div className="flex justify-content-between align-items-center">
        <div className="flex flex-column">
          <p className="line-height-2 font-medium text-xs mb-2 mt-4">
            PAYMENT OPTIONS
          </p>
          <p className="text-base line-height-3 font-semibold highlighted">
            Payment Options
          </p>
        </div>
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded p-button-sm p-3"
          onClick={handleCloseEditor}
          disabled={isUpdating}
        />
      </div>
      {editorOptions.length > 1 && (
        <StyledSelectButton
          options={editorOptions}
          value={editorMode}
          onChange={(e) => handleModeChange(e.value)}
          optionLabel="label"
          optionValue="value"
          $buttonFontSize={12}
          $buttonPaddingTop={7}
          $buttonPaddingBottom={7}
          className="mt-0"
        />
      )}

      <StyledSectionContainer className="p-3">
        {editorMode !== "downpayment" && (
          <div className="flex flex-column">
            <Label htmlFor="numberOfMonths" className="text-sm mb-2">
              Number of payments (including today&apos;s payment)
            </Label>
            <InputNumber
              id="numberOfMonths"
              value={numberOfMonths}
              onValueChange={handleChangeNumberOfMonths}
              onBlur={() => setTouched((prev) => ({ ...prev, duration: true }))}
              min={2}
              max={51}
            />
            {touched.duration && validation.errors.duration && (
              <p className="red text-sm mt-1">{validation.errors.duration}</p>
            )}
          </div>
        )}

        <div className="flex flex-column">
          <Label htmlFor="numberOfMonths" className="text-sm mb-2">
            First Payment Amount{" "}
          </Label>
          <div className="flex gap-2 w-full">
            <div className="flex flex-column">
              <Dropdown
                value={downPaymentType}
                options={[
                  { label: "%", value: "%" },
                  { label: "$", value: "$" },
                ]}
                onChange={(e) => {
                  setDownPaymentType(e.value);
                  setDownPaymentValue(null);
                  setTouched((prev) => ({ ...prev, downPayment: false }));
                }}
                optionLabel="label"
                optionValue="value"
              />
              {downPaymentType === "%" && downPaymentValue && (
                <p className="text-sm mt-1 text-gray-600">
                  = {parsePrice(previewCalculations.downPayment)}
                </p>
              )}
            </div>
            <InputNumber
              value={downPaymentDisplayValue}
              onValueChange={(e) => {
                const newValue = e.value ?? null;
                // When user changes value for flat amount, calculate total with fees for storage
                if (downPaymentType === "$" && hasClearPatientFee && newValue) {
                  const valueToStore =
                    newValue *
                    (1 + orderInfo.clearPatientFeeValueInPercentage / 100);
                  setDownPaymentValue(valueToStore);
                } else {
                  setDownPaymentValue(newValue);
                }
              }}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, downPayment: true }))
              }
              min={1}
              max={downPaymentType === "%" ? 100 : estimatedCostDisplay}
              mode={downPaymentType === "$" ? "currency" : "decimal"}
              currency="USD"
              locale="en-US"
              className="w-full"
              style={{ maxHeight: "46px" }}
            />
          </div>
          {downPaymentType === "$" &&
            hasClearPatientFee &&
            downPaymentValue && (
              <div className="mt-2">
                <span className="text-sm mb-1 text-primary">
                  with fees: {parsePrice(downPaymentValue)}{" "}
                </span>
                {downPaymentType === "$" && (
                  <span className="text-xs text-gray-600">
                    {isDownPaymentSameAsMonthlyPayment
                      ? "Same as monthly payment (default)"
                      : "Custom amount"}
                  </span>
                )}{" "}
              </div>
            )}
          {touched.downPayment && validation.errors.downPayment && (
            <p className="red text-sm mt-1">{validation.errors.downPayment}</p>
          )}
        </div>
      </StyledSectionContainer>

      {/* Preview Section */}
      <StyledSectionContainer className="p-3">
        <div className="flex justify-content-between align-items-center w-full">
          <p className="text-base highlighted font-semibold">Due Today</p>
          <p className="text-base purple font-semibold text-right">
            {parsePrice(previewCalculations.downPayment)}
          </p>
        </div>
        {!previewCalculations.isDownPaymentOnly && (
          <>
            <HorizontalLine />
            <div className="flex justify-content-between align-items-center w-full">
              <div className="flex flex-column gap-1">
                <p className="text-base font-medium line-height-3 highlighted">
                  + {parsePrice(previewCalculations.monthlyPayment)} monthly
                  payments
                </p>
                <p className="font-normal text-sm line-height-3">
                  {previewCalculations.totalPayments} payments
                </p>
              </div>
              <div className="flex flex-column">
                <p className="font-normal text-sm line-height-3 flex align-items-center">
                  <i
                    className="pi pi-calendar purple"
                    style={{ fontSize: "14px", marginRight: "4px" }}
                  />
                  Last Payment
                </p>
                <p className="text-sm font-semibold line-height-3 text-primary text-right">
                  {previewLastPaymentDate}
                </p>
              </div>
            </div>
            <HorizontalLine />
            <div className="flex justify-content-between align-items-center w-full">
              <p className="font-normal text-sm line-height-3">No Interest</p>
              <p className="font-normal text-sm line-height-3">
                Total Cost{" "}
                <span className="text-primary font-semibold text-md">
                  {parsePrice(estimatedCost)}
                </span>
              </p>
            </div>
          </>
        )}
      </StyledSectionContainer>

      <div className="flex gap-2 w-full justify-content-end">
        <Button
          label="Cancel"
          className="p-button-text p-button-sm"
          onClick={handleCloseEditor}
          disabled={isUpdating}
        />
        <Button
          label="Save"
          onClick={handleSavePaymentPlan}
          loading={isUpdating}
          disabled={!validation.isFormValid || isUpdating}
          className="p-button-sm"
        />
      </div>
    </StyledSectionContainer>
  );

  // No payment plan and not authenticated - don't show anything
  if (!hasActivePaymentPlan && !isAuthenticated) {
    return null;
  }

  return (
    <div className="flex flex-column mt-4">
      {/* When user is authenticated we always show possible actions. In case there
      is active payment plan, it can be edited. In case there is no active
      payment plan, it can be created. */}
      {isAuthenticated && !showEditor && editorOptions.length && (
        <div className="flex justify-content-center mb-2">
          <Button
            className="p-button-text p-0 gap-2"
            onClick={handleOpenEditor}
            icon="pi pi-pencil"
          >
            {hasActivePaymentPlan ? "Edit Payment Plan" : "Create Payment Plan"}
          </Button>
        </div>
      )}
      {/* Inline editor */}
      {showEditor && renderEditor()}
      {/* Payment plan display - hide when editing */}
      {!showEditor && hasActivePaymentPlan && (
        <>
          {!activePaymentPlan?.isDownPaymentOnly && (
            <div className="flex gap-2 w-full justify-content-center">
              <i className="pi pi-info-circle light-green" />
              <h3 className="text-sm light-green">
                <span className="sensitive">
                  {orderInfo.patient.firstName},
                </span>{" "}
                You&apos;ve been approved for $0 fee, 0% Interest Payment Plan!
              </h3>
            </div>
          )}
          <StyledPriceContainer className="mt-4 gap-3 flex-column">
            <div className="flex justify-content-between align-items-center w-full">
              <p className="text-lg highlighted font-semibold line-height-3">
                Due Today
              </p>
              <p className="text-xl purple font-semibold text-right line-height-4">
                {parsePrice(initialPayment)}
              </p>
            </div>
            {!isDownPaymentOnly && (
              <>
                <HorizontalLine />
                <div className="flex flex-column gap-1">
                  <p className="text-base font-medium line-height-3 highlighted">
                    + {parsePrice(monthlyPayment)} monthly payments
                  </p>
                  <p className="font-normal text-sm line-height-3 ml-3">
                    {paymentPlanDuration} payments
                  </p>
                </div>
                <StyledTimeline
                  value={paymentsToShow}
                  content={generateContent}
                />
                <HorizontalLine className="mt-2" />
                <div className="flex justify-content-between mb-2 mt-2">
                  <p className="text-sm line-height-2 font-normal">
                    No Interest
                  </p>
                  <p className="text-sm line-height-2 font-normal">
                    Total Cost {parsePrice(estimatedCost)}
                  </p>
                </div>
              </>
            )}
          </StyledPriceContainer>
        </>
      )}
    </div>
  );
}

export default PaymentPlan;
